import { companiesApi } from '../../api/companies'
import type { QboReference } from '../../api/types'
import { Alert } from '../../components/Alert'
import { BbSearchSelect } from '../../components/BbSearchSelect'
import { useCompanies } from '../../companies/useCompanies'
import { ErrorState, LoadingState } from '../../components/States'
import { useAsync } from '../../hooks/useAsync'
import { SettingsSection } from './SettingsControls'
import { useSettingsForm } from './useSettingsForm'

/* What an auto-created record looks like.

   The Mapping tab decides *whether* an import may conjure a Customer, Vendor,
   Item or Account it cannot find. This tab decides *what* it builds when it
   does. Splitting them that way is why the income account and account type
   live here rather than beside their toggles: they describe the record's
   shape, not the decision to create one.

   Every control persists and every one is read by
   ImportJobRunner.BuildReferenceRecord, which is the single place a conjured
   record is assembled. Before these fields existed it hard-coded every item as
   a Service.

   The account pickers hold a *name*, not an id. A multi-company tenant has a
   different id for the same account in each connected company, so an id would
   only ever be correct for one of them; the name is resolved against the
   target company's own chart of accounts at write time. */

/** The types QBO Online will accept on a created Item. Group ("Bundle") cannot
 *  be created by import at all, so it is deliberately absent. */
const ITEM_TYPES = [
  { value: 'Service', label: 'Service' },
  { value: 'NonInventory', label: 'Non-inventory' },
  { value: 'Inventory', label: 'Inventory' },
]

const ACCOUNT_TYPES = [
  'Bank', 'Accounts Receivable', 'Other Current Asset', 'Fixed Asset',
  'Accounts Payable', 'Credit Card', 'Other Current Liability', 'Long Term Liability',
  'Equity', 'Income', 'Cost of Goods Sold', 'Expense', 'Other Income', 'Other Expense',
]

function AccountPicker({
  id,
  label,
  help,
  accounts,
  value,
  onChange,
}: {
  id: string
  label: string
  help?: string
  accounts: QboReference[]
  value: string | null
  onChange: (next: string | null) => void
}) {
  return (
    <div className="bb-field">
      <label className="bb-flabel" htmlFor={id}>
        {label}
      </label>
      {/* Above the control, not below: that makes the control the cell's last
          element so it bottom-aligns with its neighbours automatically. */}
      {help ? <p className="bb-map-help">{help}</p> : null}
      <BbSearchSelect
        id={id}
        ariaLabel={label}
        value={value ?? ''}
        // The picker speaks in empty strings; the contract speaks in null,
        // which is what clears the default rather than storing a blank name.
        onChange={(next) => onChange(next === '' ? null : next)}
        // Collapsed to distinct names. A QuickBooks chart of accounts routinely
        // repeats a name across parents — "Job Materials" under both Income and
        // Cost of Goods Sold — and since what is stored is the name, two such
        // rows would offer the user a choice between two identical values.
        //
        // That the name alone cannot tell them apart is a real limitation of
        // storing names, kept because an account id belongs to one company and
        // this setting applies to every company on the account. Resolving a
        // repeated name picks the first match.
        options={[...new Map(accounts.map((a) => [a.name, a])).values()]
          .map((account) => ({ value: account.name, label: account.name }))}
        searchPlaceholder="Search accounts…"
      />
    </div>
  )
}

export function SettingsItemsTab() {
  const { draft, set } = useSettingsForm()
  const { current, loading: companiesLoading } = useCompanies()
  const companyId = current?.id ?? null

  const accounts = useAsync<QboReference[]>(
    () => (companyId ? companiesApi.references(companyId, 'Account') : Promise.resolve([])),
    [companyId],
  )

  if (companiesLoading) return <LoadingState label="Loading your companies…" />

  const list = accounts.data ?? []
  const isInventory = draft.defaultItemType === 'Inventory'

  // QuickBooks refuses an inventory item without both of these, so the Mapping
  // tab's "create Items" toggle stays inert until they are set. Saying so here
  // beats letting the user discover it as a batch of failed rows.
  const inventoryIncomplete =
    isInventory && (!draft.defaultItemAssetAccount || !draft.defaultItemInventoryStartDate)

  return (
    <>
      <p className="bb-map-help" style={{ marginBottom: 16 }}>
        What an import creates when your file names a product, service or account QuickBooks does
        not hold. Whether it may create them at all is on the <strong>Mapping</strong> tab.
      </p>

      {!current ? (
        <Alert tone="warn" title="No QuickBooks company connected">
          The account pickers below stay empty until a company is connected — they list that
          company's own chart of accounts. Everything else on this page still saves.
        </Alert>
      ) : accounts.error ? (
        <ErrorState error={accounts.error} onRetry={accounts.reload} />
      ) : null}

      {inventoryIncomplete ? (
        <Alert tone="warn" title="Inventory items need an asset account and a start date">
          QuickBooks rejects an inventory item without both. Until they are set, imports will not
          create missing items even when the Mapping tab allows it.
        </Alert>
      ) : null}

      <SettingsSection
        title="Defaults for Auto-Created Items"
        help="QuickBooks requires these on every new product or service, and no spreadsheet column supplies them."
      >
        <div className="bb-grid-f">
          <div className="bb-field">
            <label className="bb-flabel" htmlFor="defaultItemType">
              Item Type
            </label>
            {/* Every cell in a row carries a help line, including this one:
                the grid keeps controls bottom-aligned, so a cell with nothing
                between its label and its control reads as oddly bare. */}
            <p className="bb-map-help">
              Inventory needs extra accounts; Service needs none.
            </p>
            <select
              className="bb-select"
              id="defaultItemType"
              value={draft.defaultItemType}
              onChange={(event) => set('defaultItemType', event.target.value)}
            >
              {ITEM_TYPES.map((type) => (
                <option value={type.value} key={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <AccountPicker
            id="defaultItemIncomeAccount"
            label="Income Account"
            help="Required. QuickBooks refuses an item with no income or expense account."
            accounts={list}
            value={draft.defaultItemIncomeAccount}
            onChange={(next) => set('defaultItemIncomeAccount', next)}
          />

          <AccountPicker
            id="defaultItemExpenseAccount"
            label="COGS or Expense Account"
            help="Optional on every item type."
            accounts={list}
            value={draft.defaultItemExpenseAccount}
            onChange={(next) => set('defaultItemExpenseAccount', next)}
          />

          {/* Inventory-only, and inside this grid rather than a section of its
              own: an asset account and a start date are item defaults like the
              three above, so a separate heading said the same thing twice and
              cost the height that made this tab scroll. The grid flows them
              onto a second row.

              Hidden rather than disabled when the type is not Inventory —
              QuickBooks rejects both properties on any other type, and a
              greyed-out control invites the user to wonder what unlocks it. */}
          {isInventory ? (
            <>
              <AccountPicker
                id="defaultItemAssetAccount"
                label="Asset Account"
                help="Required for inventory. Where the stock's value is held."
                accounts={list}
                value={draft.defaultItemAssetAccount}
                onChange={(next) => set('defaultItemAssetAccount', next)}
              />

              <div className="bb-field">
                <label className="bb-flabel" htmlFor="defaultItemInventoryStartDate">
                  Inventory As-of Date
                </label>
                <p className="bb-map-help">
                  Required for inventory. New items start at a quantity of zero.
                </p>
                <input
                  className="bb-input"
                  id="defaultItemInventoryStartDate"
                  type="date"
                  value={draft.defaultItemInventoryStartDate ?? ''}
                  onChange={(event) =>
                    set('defaultItemInventoryStartDate', event.target.value || null)
                  }
                />
              </div>
            </>
          ) : null}
        </div>
      </SettingsSection>

      <SettingsSection
        title="Defaults for Auto-Created Accounts"
        help="Used when an import creates an account your file refers to but QuickBooks does not hold."
      >
        <div className="bb-grid-f">
          <div className="bb-field">
            <label className="bb-flabel" htmlFor="defaultAccountType">
              Account Type
            </label>
            <p className="bb-map-help">
              QuickBooks requires a type on every account.
            </p>
            <select
              className="bb-select"
              id="defaultAccountType"
              value={draft.defaultAccountType ?? ''}
              onChange={(event) => set('defaultAccountType', event.target.value || null)}
            >
              <option value="">Do not create accounts</option>
              {ACCOUNT_TYPES.map((type) => (
                <option value={type} key={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div className="bb-field">
            <label className="bb-flabel" htmlFor="defaultAccountDetailType">
              Account Detail Type
            </label>
            <p className="bb-map-help">
              Optional. QuickBooks derives one from the account type when left blank.
            </p>
            <input
              className="bb-input"
              id="defaultAccountDetailType"
              type="text"
              placeholder="Derived from the type"
              value={draft.defaultAccountDetailType ?? ''}
              onChange={(event) => set('defaultAccountDetailType', event.target.value || null)}
            />
          </div>
        </div>
      </SettingsSection>
    </>
  )
}
